import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getOffersByProductId, getProductBySlug } from '../../utils/api';
import { getDisplayImageSrc } from '../PriceComparison/simpleProductPageShared';
import RetailerLogo from '../RetailerLogo';
import {
  AFFILIATE_PAGE_SOURCE,
  trackPricedAffiliateDealClick,
  trackPrimaryCtaClick,
} from '../../utils/enhancedAnalytics';
import { usePriceSectionView } from '../../utils/usePriceSectionView';
import { usePageSeo } from '../../seo/usePageSeo';
import {
  COMPARISON_RETAILER_CTA,
  filterValidComparisonOffers,
  formatComparisonTimestamp,
  formatOfferPrice,
  getComparableOfferPrice,
  getComparisonPriceBoard,
  getOfferLink,
  getOfferListPresentation,
  getRetailerNameFromOffer,
} from './comparisonOffers';
import { getComparisonProducts } from './comparisonConfigs';
import {
  buildThreeModelSpecRows,
  getComparisonSourceLinks,
  getHdVariantFacts,
  getLiveScopeSpecLabel,
  getManufacturerConditionNote,
  LIVESCOPE_SPEC_KEYS,
} from './comparisonVerifiedSpecs';
import './ProductVsComparisonPage.css';
import AmazonPriceNotice from '../PriceComparison/AmazonPriceNotice';

const INITIAL_SIDE = {
  loadingProduct: true,
  loadingOffers: false,
  product: null,
  productError: null,
  offers: [],
  offersError: null,
};

const AFFILIATE_DISCLOSURE =
  'Angler Built may earn a commission when you purchase through retailer links, at no additional cost to you.';

function createInitialSides(products) {
  return products.reduce((acc, product) => {
    acc[product.key] = { ...INITIAL_SIDE };
    return acc;
  }, {});
}

function productPdpPath(product) {
  return product?.pdpPath || (product?.slug ? `/simple-products/${product.slug}` : '/price-comparison');
}

function trackComparisonAffiliateClick({
  retailer,
  href,
  offer,
  productSlug,
  comparisonId,
}) {
  try {
    trackPricedAffiliateDealClick({
      retailer,
      affiliateUrl: href,
      salePrice: getComparableOfferPrice(offer),
      pageSource: AFFILIATE_PAGE_SOURCE.PRODUCT_COMPARISON,
      productSlug,
      comparisonId,
      offerId: offer?.id || offer?.offer_id,
      placement: 'current_prices',
    });
  } catch {
    // Analytics must never block outbound navigation.
  }
}

function RetailerMark({ retailer, className = 'ab-cmp-retailer-logo' }) {
  return <RetailerLogo name={retailer} className={className} />;
}

function retailerCtaLabel(productLabel, retailer) {
  return `Check price and availability for ${productLabel} at ${retailer}`;
}

function RetailerControl({
  retailer,
  href,
  productLabel,
  offer,
  productSlug,
  comparisonId,
  testId,
}) {
  const inner = (
    <>
      <RetailerMark retailer={retailer} />
      <span className="ab-cmp-retailer-ext" aria-hidden="true">↗</span>
      <span className="ab-cmp-sr-only">{COMPARISON_RETAILER_CTA}</span>
    </>
  );

  if (!href) {
    return <span className="ab-cmp-retailer-action is-static">{inner}</span>;
  }

  return (
    <a
      className="ab-cmp-retailer-action"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={retailerCtaLabel(productLabel, retailer)}
      data-testid={testId}
      onClick={() => {
        trackComparisonAffiliateClick({
          retailer,
          href,
          offer,
          productSlug,
          comparisonId,
        });
      }}
    >
      {inner}
    </a>
  );
}

async function loadComparisonSide(slug, setSides, sideKey, cancelled) {
  const patch = (partial) => {
    if (cancelled()) return;
    setSides((prev) => ({
      ...prev,
      [sideKey]: { ...prev[sideKey], ...partial },
    }));
  };

  let product;
  try {
    product = await getProductBySlug(slug);
  } catch (error) {
    patch({
      loadingProduct: false,
      loadingOffers: false,
      product: null,
      productError: error?.message || 'product_failed',
      offers: [],
      offersError: null,
    });
    return;
  }

  if (!product) {
    patch({
      loadingProduct: false,
      loadingOffers: false,
      product: null,
      productError: 'not_found',
      offers: [],
      offersError: null,
    });
    return;
  }

  patch({
    loadingProduct: false,
    loadingOffers: true,
    product,
    productError: null,
  });

  try {
    const result = await getOffersByProductId(product.id);
    patch({
      loadingOffers: false,
      offers: filterValidComparisonOffers(result?.data),
      offersError: null,
    });
  } catch (error) {
    patch({
      loadingOffers: false,
      offers: [],
      offersError: error?.message || 'offers_failed',
    });
  }
}

function ProductImage({ product, shortLabel, fallbackSrc, size = 'hero' }) {
  const src = getDisplayImageSrc(fallbackSrc) || getDisplayImageSrc(product?.image_url);
  if (!src) {
    return (
      <div className="ab-cmp-photo-placeholder" data-testid={`image-placeholder-${shortLabel}`}>
        {shortLabel} image unavailable
      </div>
    );
  }
  return (
    <img
      className={size === 'hero' ? 'ab-cmp-hero-photo' : 'ab-cmp-photo'}
      src={src}
      alt={product?.name || shortLabel}
      data-testid={`image-${shortLabel}`}
    />
  );
}

function OfferLine({
  sideKey,
  index,
  productLabel,
  row,
  comparisonId,
  productSlug,
}) {
  const { offer, retailer, href, presentation, isTiedBest } = row;
  const checked = presentation.isCurrent
    ? formatComparisonTimestamp(offer, 'Updated')
    : formatComparisonTimestamp(offer, 'Last checked');
  const staleNote = !presentation.isCurrent
    ? presentation.disclaimer || 'Price may have changed'
    : null;
  const className = [
    'ab-cmp-offer-line',
    isTiedBest ? 'is-best' : '',
    presentation.isCurrent ? '' : 'is-stale',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      data-testid={`other-offer-${sideKey}-${index}`}
      data-offer-state={presentation.state}
    >
      <span className="ab-cmp-offer-line-price">
        {presentation.priceLabel || formatOfferPrice(offer)}
      </span>
      <RetailerControl
        retailer={retailer}
        href={href}
        productLabel={productLabel}
        offer={offer}
        productSlug={productSlug}
        comparisonId={comparisonId}
        testId={`other-cta-${sideKey}-${index}`}
      />
      <span className="ab-cmp-offer-line-meta">
        {checked}
        {staleNote ? ` · ${staleNote}` : ''}
      </span>
    </div>
  );
}

function CompactCurrentOffer({
  sideKey,
  productConfig,
  side,
  comparisonId,
}) {
  const loading = side.loadingProduct || side.loadingOffers;
  const pdpHref = productPdpPath(productConfig);

  if (loading) {
    return (
      <div className="ab-cmp-offer-compact" data-testid={`compact-offer-${sideKey}`}>
        <p className="ab-cmp-muted" data-testid={`offers-loading-${sideKey}`}>
          Loading current retailer offers…
        </p>
      </div>
    );
  }

  if (side.offersError) {
    return (
      <div className="ab-cmp-offer-compact" data-testid={`compact-offer-${sideKey}`}>
        <p className="ab-cmp-error" data-testid={`offers-error-${sideKey}`}>
          Current price unavailable
        </p>
        <Link className="ab-cmp-text-link" to={pdpHref} data-testid={`pdp-${sideKey}`}>
          View retailer options
        </Link>
      </div>
    );
  }

  const board = getComparisonPriceBoard(side.offers);

  if (!board.hasCurrent || !board.offer || !board.href) {
    return (
      <div
        className="ab-cmp-offer-compact"
        data-testid={`compact-offer-${sideKey}`}
        data-offer-state="none"
      >
        <p className="ab-cmp-offer-kicker" data-testid={`offers-empty-${sideKey}`}>
          Current price unavailable
        </p>
        <Link className="ab-cmp-text-link" to={pdpHref} data-testid={`pdp-${sideKey}`}>
          View retailer options
        </Link>
      </div>
    );
  }

  return (
    <div
      className="ab-cmp-offer-compact"
      data-testid={`compact-offer-${sideKey}`}
      data-offer-state="CURRENT"
    >
      <div className="ab-cmp-price-lead">
        <p className="ab-cmp-offer-price">{board.priceLabel}</p>
        <RetailerControl
          retailer={board.retailer}
          href={board.href}
          productLabel={productConfig.shortLabel}
          offer={board.offer}
          productSlug={productConfig.slug}
          comparisonId={comparisonId}
          testId={`retailer-cta-${sideKey}`}
        />
      </div>
      {board.freshness ? (
        <p className="ab-cmp-offer-meta" data-testid={`freshness-${sideKey}`}>
          {board.freshness}
        </p>
      ) : null}
      {board.remainingRows.length ? (
        <div className="ab-cmp-offer-lines" data-testid={`other-retailers-${sideKey}`}>
          {board.remainingRows.map((row, index) => (
            <OfferLine
              key={`${row.retailer}-${row.href || index}`}
              sideKey={sideKey}
              index={index}
              productLabel={productConfig.shortLabel}
              row={row}
              comparisonId={comparisonId}
              productSlug={productConfig.slug}
            />
          ))}
        </div>
      ) : null}
      <AmazonPriceNotice offers={side.offers} />
      <Link className="ab-cmp-text-link ab-cmp-text-link-block" to={pdpHref} data-testid={`pdp-${sideKey}`}>
        View product page
      </Link>
    </div>
  );
}

function OfferList({
  sideKey,
  shortLabel,
  emptyOffersMessage,
  side,
  productSlug,
  comparisonId,
}) {
  if (side.loadingProduct || side.loadingOffers) {
    return (
      <p className="ab-cmp-muted" data-testid={`offers-list-loading-${sideKey}`}>
        Loading current retailer offers…
      </p>
    );
  }

  if (side.offersError) {
    return (
      <p className="ab-cmp-error" data-testid={`offers-list-error-${sideKey}`}>
        Current retailer offers could not be loaded for {shortLabel}. The comparison
        above is still valid.
      </p>
    );
  }

  if (!side.offers.length) {
    return (
      <p className="ab-cmp-muted" data-testid={`offers-empty-${sideKey}`}>
        {emptyOffersMessage}
      </p>
    );
  }

  return (
    <div className="ab-cmp-offer-rows" data-testid={`offers-${sideKey}`}>
      <AmazonPriceNotice offers={side.offers} />
      {side.offers.map((offer, index) => {
        const retailer = getRetailerNameFromOffer(offer);
        const href = getOfferLink(offer);
        const presentation = getOfferListPresentation(offer);
        const ctaLabel = COMPARISON_RETAILER_CTA;
        return (
          <article
            key={`${retailer}-${href || index}`}
            className="ab-cmp-offer-row"
            data-testid={`offer-${sideKey}-${index}`}
            data-offer-state={presentation.state}
          >
            <div className="ab-cmp-offer-row-top">
              <div>
                <RetailerLogo name={retailer} className="ab-cmp-retailer-logo" />
                <p className="ab-cmp-offer-retailer">{retailer}</p>
              </div>
              <p className="ab-cmp-offer-row-price">
                {presentation.priceLabel || formatOfferPrice(offer)}
              </p>
            </div>
            {(presentation.meta || presentation.disclaimer) && (
              <p className="ab-cmp-offer-row-meta">
                {presentation.meta}
                {presentation.meta && presentation.disclaimer ? ' · ' : null}
                {presentation.disclaimer}
              </p>
            )}
            {href ? (
              <a
                className={presentation.isCurrent ? 'ab-cmp-buy' : 'ab-cmp-buy ab-cmp-buy-secondary'}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={retailerCtaLabel(shortLabel, retailer)}
                data-testid={`offer-cta-${sideKey}-${index}`}
                onClick={() => {
                  trackComparisonAffiliateClick({
                    retailer,
                    href,
                    offer,
                    productSlug,
                    comparisonId,
                  });
                }}
              >
                {ctaLabel}
              </a>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

const RANGE_LAYOUT_MOBILE = {
  width: 360,
  height: 220,
  left: 12,
  track: 336,
  tickY: 20,
  axisY: 28,
  hdLabelY: 50,
  hdBarY: 56,
  hdBarH: 32,
  lvsLabelY: 124,
  lvsBarY: 130,
  lvsBarH: 32,
  lineTop: 24,
  lineBottom: 174,
  zoneY: 32,
  zoneH: 142,
  endInset: 8,
  hdEndInset: 14,
  tick100Anchor: 'end',
  tick100Dx: -5,
  tick125Anchor: 'start',
  tick125Dx: 5,
};

const RANGE_LAYOUT_DESKTOP = {
  width: 960,
  height: 172,
  left: 28,
  track: 904,
  tickY: 18,
  axisY: 26,
  hdLabelY: 46,
  hdBarY: 54,
  hdBarH: 22,
  lvsLabelY: 104,
  lvsBarY: 112,
  lvsBarH: 22,
  lineTop: 22,
  lineBottom: 148,
  zoneY: 30,
  zoneH: 118,
  endInset: 10,
  tick100Anchor: 'middle',
  tick100Dx: 0,
  tick125Anchor: 'middle',
  tick125Dx: 0,
};

function RangeChartSvg({ range, layout, className, testId }) {
  const x = (ft) => layout.left + (ft / range.scaleMax) * layout.track;
  const x0 = x(0);
  const x100 = x(range.anglerBuiltMarkerFt);
  const x125 = x(range.hdMaxFt);
  const x250 = x(range.lvs44MaxFt);
  const hdMid = layout.hdBarY + layout.hdBarH / 2 + 4;
  const lvsMid = layout.lvsBarY + layout.lvsBarH / 2 + 4;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      data-testid={testId}
    >
      <rect
        className="ab-cmp-range-zone"
        data-testid={`${testId}-zone`}
        x={x100}
        y={layout.zoneY}
        width={x125 - x100}
        height={layout.zoneH}
        fill="#e8eef9"
      />
      <line x1={x0} y1={layout.axisY} x2={x250} y2={layout.axisY} stroke="#9aa8bc" strokeWidth="1.5" />
      <text x={x0} y={layout.tickY} textAnchor="start" className="ab-cmp-svg-tick">0 ft</text>
      <text
        x={x100 + (layout.tick100Dx || 0)}
        y={layout.tickY}
        textAnchor={layout.tick100Anchor || 'middle'}
        className="ab-cmp-svg-tick"
      >
        100 ft
      </text>
      <text
        x={x125 + (layout.tick125Dx || 0)}
        y={layout.tickY}
        textAnchor={layout.tick125Anchor || 'middle'}
        className="ab-cmp-svg-tick"
      >
        125 ft
      </text>
      <text x={x250} y={layout.tickY} textAnchor="end" className="ab-cmp-svg-tick">250 ft</text>
      <text x={x0} y={layout.hdLabelY} className="ab-cmp-svg-path">{range.hdPathLabelShort}</text>
      <text x={x0} y={layout.lvsLabelY} className="ab-cmp-svg-path">{range.lvs44PathLabel}</text>
      <rect x={x0} y={layout.hdBarY} width={x125 - x0} height={layout.hdBarH} fill="#0f50f2" rx="3" />
      <rect x={x0} y={layout.lvsBarY} width={x250 - x0} height={layout.lvsBarH} fill="#132033" rx="3" />
      <line
        className="ab-cmp-range-bound-100"
        x1={x100}
        y1={layout.lineTop}
        x2={x100}
        y2={layout.lineBottom}
        stroke="#0f50f2"
        strokeWidth="2.25"
        strokeDasharray="4 3"
      />
      <line
        className="ab-cmp-range-bound-125"
        x1={x125}
        y1={layout.lineTop}
        x2={x125}
        y2={layout.lineBottom}
        stroke="#0f50f2"
        strokeWidth="2.25"
        strokeDasharray="4 3"
      />
      <text
        x={x125 - (layout.hdEndInset ?? layout.endInset)}
        y={hdMid}
        textAnchor="end"
        className="ab-cmp-svg-end-light"
        data-testid={`${testId}-hd-end`}
      >
        125 ft
      </text>
      <text
        x={x250 - layout.endInset}
        y={lvsMid}
        textAnchor="end"
        className="ab-cmp-svg-end-light"
        data-testid={`${testId}-lvs-end`}
      >
        250 ft
      </text>
    </svg>
  );
}

function RangeGraphic({ range }) {
  return (
    <figure className="ab-cmp-range" data-testid="range-graphic">
      <p className="ab-cmp-sr-only" id="range-a11y" data-testid="range-a11y">
        {range.accessibleTitle}. {range.accessibleDescription}
      </p>
      <RangeChartSvg
        range={range}
        layout={RANGE_LAYOUT_MOBILE}
        className="ab-cmp-range-svg ab-cmp-range-mobile"
        testId="range-chart-mobile"
      />
      <RangeChartSvg
        range={range}
        layout={RANGE_LAYOUT_DESKTOP}
        className="ab-cmp-range-svg ab-cmp-range-desktop"
        testId="range-chart-desktop"
      />
    </figure>
  );
}

function SeparationGraphic({ range }) {
  const hdSep = getLiveScopeSpecLabel('livescope-2-lvs42hd', LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN);
  const lvs44Sep = getLiveScopeSpecLabel('livescope-2-lvs44', LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN);

  return (
    <section className="ab-cmp-sep" data-testid="separation-graphic">
      <h3 className="ab-cmp-graphic-title">Close-range target separation at 30 ft</h3>
      <p className="ab-cmp-relative" data-testid="separation-note">{range.lowerIsBetter}</p>
      <p className="ab-cmp-sep-pair" data-testid="sep-pair">{range.pairStory}</p>
      <div
        className="ab-cmp-sep-figure"
        role="img"
        title="Close-range target separation at 30 ft for LVS42HD, LVS42HD-IF, and LVS44"
        aria-label="If two targets are about 4.5 inches apart at 30 feet, LVS42HD and LVS42HD-IF resolve the pair as two distinct returns. LVS44 may merge the pair. Dark dots are the physical targets. Blue shapes are the sonar returns. Smaller minimum separation is better. This is not a literal sonar screenshot."
      >
        <div className="ab-cmp-sep-panel" data-testid="sep-hd">
          <div className="ab-cmp-sep-visual">
            <p className="ab-cmp-sep-model">LVS42HD / LVS42HD-IF</p>
            <svg className="ab-cmp-sep-mini" viewBox="0 0 160 88" aria-hidden="true">
              <defs>
                <linearGradient id="ab-sep-hd-return" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f50f2" />
                  <stop offset="100%" stopColor="#6d9bff" />
                </linearGradient>
              </defs>
              <circle className="ab-cmp-sep-target" cx="48" cy="20" r="8" fill="#132033" />
              <circle className="ab-cmp-sep-target" cx="112" cy="20" r="8" fill="#132033" />
              <ellipse className="ab-cmp-sep-return ab-cmp-sep-return-hd" cx="48" cy="62" rx="20" ry="15" fill="url(#ab-sep-hd-return)" />
              <ellipse className="ab-cmp-sep-return ab-cmp-sep-return-hd" cx="112" cy="62" rx="20" ry="15" fill="url(#ab-sep-hd-return)" />
            </svg>
          </div>
          <p className="ab-cmp-sep-result">{range.hdSepResult}</p>
          <p className="ab-cmp-sep-spec">{hdSep} minimum separation</p>
        </div>
        <div className="ab-cmp-sep-panel" data-testid="sep-lvs44">
          <div className="ab-cmp-sep-visual">
            <p className="ab-cmp-sep-model">LVS44</p>
            <svg className="ab-cmp-sep-mini" viewBox="0 0 160 88" aria-hidden="true">
              <defs>
                <linearGradient id="ab-sep-lvs44-return" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f50f2" />
                  <stop offset="100%" stopColor="#6d9bff" />
                </linearGradient>
              </defs>
              <circle className="ab-cmp-sep-target" cx="48" cy="20" r="8" fill="#132033" />
              <circle className="ab-cmp-sep-target" cx="112" cy="20" r="8" fill="#132033" />
              <ellipse
                className="ab-cmp-sep-return ab-cmp-sep-return-merged"
                cx="80"
                cy="62"
                rx="56"
                ry="18"
                fill="url(#ab-sep-lvs44-return)"
              />
            </svg>
          </div>
          <p className="ab-cmp-sep-result">{range.lvs44SepResult}</p>
          <p className="ab-cmp-sep-spec">{lvs44Sep} minimum separation</p>
        </div>
      </div>
      <p className="ab-cmp-sep-note" data-testid="sep-illustration-note">{range.illustrationNote}</p>
      <p className="ab-cmp-sep-equal" data-testid="equal-sep-100">{range.equalSeparationNote}</p>
    </section>
  );
}

function StatusIcon({ kind }) {
  return (
    <span
      className={`ab-cmp-status-icon is-${kind}`}
      aria-hidden="true"
      data-status={kind}
    >
      <svg viewBox="0 0 32 32">
        {kind === 'check' ? (
          <path
            d="M8 17l5 5L24 10"
            fill="none"
            stroke="#15803d"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <>
            <path d="M10 10l12 12" fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
            <path d="M22 10L10 22" fill="none" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
      </svg>
    </span>
  );
}

function IceHoleOutcome({ inches, fit, label, model }) {
  const diameter = inches === 8 ? 96 : 72;
  return (
    <div
      className={`ab-cmp-ice-outcome ab-cmp-ice-${inches} ${fit ? 'is-fit' : 'is-no'}`}
      data-inches={inches}
    >
      <p className="ab-cmp-ice-size">{inches} in</p>
      <div
        className={`ab-cmp-ice-stage ab-cmp-ice-stage-${inches}`}
        data-diameter={diameter}
      >
        <svg className="ab-cmp-ice-svg" viewBox="0 0 96 96" aria-hidden="true">
          <circle cx="48" cy="48" r="47" fill="#eef5f9" stroke="#c5d3de" strokeWidth="1.5" />
          <circle cx="48" cy="48" r="43" fill="#d7e4ee" />
          <circle cx="48" cy="48" r="38" fill="#c3d3e0" />
          <circle cx="18" cy="22" r="2.2" fill="#ffffff" opacity="0.85" />
          <circle cx="30" cy="16" r="1.6" fill="#ffffff" opacity="0.7" />
          <circle cx="70" cy="20" r="2" fill="#ffffff" opacity="0.8" />
          <circle cx="80" cy="32" r="1.4" fill="#ffffff" opacity="0.65" />
          <circle cx="20" cy="74" r="1.8" fill="#ffffff" opacity="0.7" />
          <circle cx="76" cy="76" r="2" fill="#ffffff" opacity="0.75" />
          <circle cx="48" cy="48" r="32" fill="#0b2740" />
        </svg>
        <svg
          className={`ab-cmp-ice-unit ${model === 'lvs44' ? 'ab-cmp-ice-unit-lvs44' : 'ab-cmp-ice-unit-hd'}`}
          viewBox="0 0 54 24"
          aria-hidden="true"
        >
          <rect x="24" y="0" width="6" height="5" rx="1" fill="#111" />
          <rect x="2" y="5" width="50" height="16" rx="4" fill="#1b1b1b" />
          <circle cx="14" cy="13" r="2.1" fill="#5a5a5a" />
          <circle cx="22" cy="13" r="2.1" fill="#5a5a5a" />
          <circle cx="30" cy="13" r="2.1" fill="#5a5a5a" />
          <rect x="40" y="10" width="10" height="6" rx="1.5" fill="#111" />
        </svg>
      </div>
      <StatusIcon kind={fit ? 'check' : 'x'} />
      <p className="ab-cmp-ice-label">{label}</p>
    </div>
  );
}

function IceHoleGraphic({ iceHole }) {
  return (
    <section className="ab-cmp-hole" data-testid="ice-hole-graphic">
      <h3 className="ab-cmp-graphic-title">{iceHole.heading}</h3>
      <div className="ab-cmp-hole-compare">
        <div className="ab-cmp-hole-path" data-testid="ice-hole-hd">
          <p className="ab-cmp-hole-model">{iceHole.hdFitLabel}</p>
          <IceHoleOutcome inches={6} fit label={iceHole.hdFitStatus} model="hd" />
        </div>
        <div className="ab-cmp-hole-path" data-testid="ice-hole-lvs44">
          <p className="ab-cmp-hole-model">LVS44</p>
          <div className="ab-cmp-hole-pair">
            <IceHoleOutcome inches={6} fit={false} label={iceHole.lvs44FailStatus} model="lvs44" />
            <span className="ab-cmp-hole-arrow" aria-hidden="true">→</span>
            <IceHoleOutcome inches={8} fit label={iceHole.lvs44EightStatus} model="lvs44" />
          </div>
        </div>
      </div>
      <p className="ab-cmp-sr-only" data-testid="ice-hole-wording">{iceHole.summary}</p>
      {iceHole.sourceNote ? (
        <p className="ab-cmp-sr-only" data-testid="ice-hole-method">{iceHole.sourceNote}</p>
      ) : null}
    </section>
  );
}

function MeasureBar({ label, value, max, testId }) {
  const numeric = Number.parseFloat(String(value));
  const width = Number.isFinite(numeric) && max ? Math.max(18, (numeric / max) * 100) : 18;
  return (
    <div className="ab-cmp-measure" data-testid={testId}>
      <p className="ab-cmp-measure-label">{label}</p>
      <div className="ab-cmp-measure-track" aria-hidden="true">
        <span className="ab-cmp-measure-fill" style={{ width: `${width}%` }} />
      </div>
      <p className="ab-cmp-measure-value">{value}</p>
    </div>
  );
}

function HdVariantCompare({ productHd, productIce, hdVariant }) {
  const hd = getHdVariantFacts(productHd.slug);
  const ice = getHdVariantFacts(productIce.slug);
  return (
    <div className="ab-cmp-hd" data-testid="hd-variant">
      <p className="ab-cmp-hd-skim" data-testid="hd-skim">{hdVariant.skimLine}</p>
      <div className="ab-cmp-hd-grid">
        <article className="ab-cmp-hd-side" data-testid="hd-variant-standard">
          <h3>{productHd.shortLabel}</h3>
          <p className="ab-cmp-hd-best">Best for: {productHd.bestFor}</p>
          <MeasureBar label="Cable" value={hd.cableLength} max={18} testId="cable-hd" />
          <MeasureBar label="Weight including cable" value={hd.weight} max={3.6} testId="weight-hd" />
          <p className="ab-cmp-hd-fact">{hd.cableType}</p>
          <p className="ab-cmp-hd-fact">{hd.mounts}</p>
        </article>
        <article className="ab-cmp-hd-side" data-testid="hd-variant-ice">
          <h3>{productIce.shortLabel}</h3>
          <p className="ab-cmp-hd-best">Best for: {productIce.bestFor}</p>
          <MeasureBar label="Cable" value={ice.cableLength} max={18} testId="cable-ice" />
          <MeasureBar label="Weight including cable" value={ice.weight} max={3.6} testId="weight-ice" />
          <p className="ab-cmp-hd-fact">{ice.cableType}</p>
          <p className="ab-cmp-hd-fact">{ice.mounts}</p>
        </article>
      </div>
      <p className="ab-cmp-hd-shared" data-testid="hd-shared">{hdVariant.sharedStatement}</p>
    </div>
  );
}

function SpecTable3({ rows, labels, testIdPrefix = 'spec' }) {
  return (
    <div className="ab-cmp-table ab-cmp-table-3">
      <div className="ab-cmp-spec-head ab-cmp-spec-head-3" aria-hidden="true">
        <span>Factor</span>
        {labels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      {rows.map((row) => (
        <div key={row.id} className="ab-cmp-row ab-cmp-row-3" data-testid={`${testIdPrefix}-${row.id}`}>
          <p className="ab-cmp-row-name">{row.label}</p>
          <div className="ab-cmp-row-pair ab-cmp-row-triple">
            {row.values.map((value, index) => (
              <div className="ab-cmp-row-value" key={`${row.id}-${labels[index]}`}>
                <span className="ab-cmp-side-label">{labels[index]}</span>
                <span className="ab-cmp-row-num">{value}</span>
              </div>
            ))}
          </div>
          {row.note ? <p className="ab-cmp-row-note">{row.note}</p> : null}
        </div>
      ))}
    </div>
  );
}

export default function ProductVsComparisonPage({ config }) {
  const products = useMemo(() => getComparisonProducts(config), [config]);
  const [sides, setSides] = useState(() => createInitialSides(products));
  const commerceRef = useRef(null);
  const loadedOfferCount = products.reduce((count, product) => {
    const side = sides[product.key];
    if (!side || side.loadingProduct || side.loadingOffers || side.offersError) return count;
    return count + (Array.isArray(side.offers) ? side.offers.length : 0);
  }, 0);
  usePriceSectionView(
    commerceRef,
    {
      pageSource: AFFILIATE_PAGE_SOURCE.PRODUCT_COMPARISON,
      productSlug: config?.productHd?.slug,
      comparisonId: config?.id,
      retailerCount: loadedOfferCount,
      hasCurrentVerifiedPrice: loadedOfferCount > 0,
      placement: 'current_prices',
    },
    Boolean(config?.id) && loadedOfferCount > 0
  );

  usePageSeo({
    title: config?.documentTitle,
    description: config?.metaDescription,
    path: config?.path,
  });

  useEffect(() => {
    if (!products.length) return undefined;
    let cancelled = false;
    const isCancelled = () => cancelled;
    setSides(createInitialSides(products));
    products.forEach((product) => {
      loadComparisonSide(product.slug, setSides, product.key, isCancelled);
    });
    return () => {
      cancelled = true;
    };
  }, [products]);

  const specRows = useMemo(
    () =>
      buildThreeModelSpecRows(
        config?.productHd?.slug,
        config?.productIce?.slug,
        config?.productLvs44?.slug
      ),
    [config]
  );
  const sourceLinks = useMemo(() => getComparisonSourceLinks(), []);
  const conditionNote = getManufacturerConditionNote();

  if (!config) return null;

  const { productHd, productIce, productLvs44, rangeDecision, iceHole, hdVariant } = config;
  const disclosure = config.affiliateDisclosure || AFFILIATE_DISCLOSURE;
  const allRetailerPricesId = config.allRetailerPricesId || 'all-retailer-prices';
  const compatibilityPath = config.compatibilityPath || '/compatibility';

  return (
    <article className="ab-cmp" data-testid="comparison-page" data-comparison-id={config.id}>
      <header className="ab-cmp-hero">
        <div className="ab-cmp-hero-inner">
          <div className="ab-cmp-hero-copy">
            <h1 className="ab-cmp-title">{config.pageTitle}</h1>
            <p className="ab-cmp-model-line" data-testid="model-line">{config.modelLine}</p>
            <p className="ab-cmp-lede-line">{config.supportingLine}</p>
          </div>
          <div className="ab-cmp-trio" data-testid="product-trio">
            {products.map((product) => (
              <figure key={product.key} className="ab-cmp-trio-item">
                <ProductImage
                  product={sides[product.key]?.product}
                  shortLabel={product.shortLabel}
                  fallbackSrc={product.imageFallback}
                />
                <figcaption>{product.shortLabel}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </header>

      <section className="ab-cmp-stage" data-testid="stage-1">
        <div className="ab-cmp-inner">
          <h2 className="ab-cmp-stage-title">{config.stage1Heading}</h2>
          <RangeGraphic range={rangeDecision} />
          <ol
            className="ab-cmp-range-summary"
            data-testid="range-summary"
            data-mobile-layout="stacked"
            data-desktop-layout="columns"
          >
            {rangeDecision.summary.map((item) => (
              <li key={item.id} data-testid={`range-summary-${item.id}`}>
                <strong>{item.label}</strong>
                <span className={item.mobileAnswer ? 'ab-cmp-range-answer-desktop' : undefined}>
                  {item.answer}
                </span>
                {item.mobileAnswer ? (
                  <span className="ab-cmp-range-answer-mobile">{item.mobileAnswer}</span>
                ) : null}
              </li>
            ))}
          </ol>
          <SeparationGraphic range={rangeDecision} />
          <IceHoleGraphic iceHole={iceHole} />
        </div>
      </section>

      <section className="ab-cmp-stage ab-cmp-stage-light" data-testid="stage-2">
        <div className="ab-cmp-inner">
          <h2 className="ab-cmp-stage-title">{config.stage2Heading}</h2>
          <HdVariantCompare
            productHd={productHd}
            productIce={productIce}
            hdVariant={hdVariant}
          />
        </div>
      </section>

      <section ref={commerceRef} className="ab-cmp-commerce" data-testid="commerce">
        <div className="ab-cmp-inner">
          <h2 className="ab-cmp-stage-title">{config.pricesHeading}</h2>
          <div className="ab-cmp-commerce-3">
            {products.map((product) => (
              <article
                key={product.key}
                className="ab-cmp-commerce-item ab-cmp-price-card"
                data-testid={`comparison-side-${product.key}`}
                data-canonical-slug={product.slug}
              >
                <h3>{product.shortLabel}</h3>
                {sides[product.key]?.productError ? (
                  <p className="ab-cmp-error" data-testid={`product-error-${product.key}`}>
                    {product.shortLabel} details could not be loaded from the product catalog.
                  </p>
                ) : null}
                <CompactCurrentOffer
                  sideKey={product.key}
                  productConfig={product}
                  side={sides[product.key] || INITIAL_SIDE}
                  comparisonId={config.id}
                />
              </article>
            ))}
          </div>
          <p className="ab-cmp-disclosure" data-testid="affiliate-disclosure">
            {disclosure}
          </p>
        </div>
      </section>

      <section className="ab-cmp-compat-wrap">
        <div className="ab-cmp-inner">
          <aside className="ab-cmp-compat" data-testid="compatibility-callout">
            <h2 className="ab-cmp-compat-title">{config.compatibilityHeading}</h2>
            <p>{config.compatibilityLine}</p>
            <Link
              className="ab-cmp-secondary"
              to={compatibilityPath}
              data-testid="check-compatibility"
              onClick={() => {
                trackPrimaryCtaClick({
                  pageSource: AFFILIATE_PAGE_SOURCE.PRODUCT_COMPARISON,
                  placement: 'compatibility_callout',
                  cta: 'check_compatibility',
                  comparisonId: config.id,
                });
              }}
            >
              Check Compatibility
            </Link>
          </aside>
        </div>
      </section>

      <section className="ab-cmp-more" data-testid="more-detail">
        <div className="ab-cmp-inner">
          <h2 className="ab-cmp-stage-title">More detail</h2>

          <details className="ab-cmp-disclosure-panel" data-testid="full-specs-disclosure">
            <summary>Full performance specifications and Garmin sources</summary>
            <div className="ab-cmp-disclosure-body">
              <SpecTable3
                rows={specRows}
                labels={[productHd.shortLabel, productIce.shortLabel, productLvs44.shortLabel]}
                testIdPrefix="full-spec"
              />
              <p className="ab-cmp-source-note">
                Garmin currently positions the IF model as the same HD sonar path as LVS42HD.
              </p>
              {conditionNote ? <p className="ab-cmp-source-note">{conditionNote}</p> : null}
              <p className="ab-cmp-source-note">
                Specifications are Garmin-published manufacturer facts unless labeled as Angler Built field guidance. Angler Built did not independently measure these values.
              </p>
              <ul className="ab-cmp-sources">
                {sourceLinks.map((source) => (
                  <li key={source.id}>
                    <a href={source.href} target="_blank" rel="noopener noreferrer">
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </details>

          <details className="ab-cmp-disclosure-panel" data-testid="guidance-disclosure">
            <summary>{config.whyHeading}</summary>
            <div className="ab-cmp-disclosure-body">
              <ul className="ab-cmp-why-list">
                {(config.why || []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {config.guidanceNote ? (
                <p className="ab-cmp-source-note">{config.guidanceNote}</p>
              ) : null}
              {iceHole.sourceNote ? (
                <p className="ab-cmp-source-note">{iceHole.sourceNote}</p>
              ) : null}
            </div>
          </details>

          <details
            className="ab-cmp-disclosure-panel"
            id={allRetailerPricesId}
            data-testid="all-prices-disclosure"
          >
            <summary>All current retailer prices</summary>
            <div className="ab-cmp-disclosure-body">
              <div className="ab-cmp-commerce-grid ab-cmp-commerce-grid-3">
                {products.map((product) => (
                  <div key={product.key}>
                    <h3 className="ab-cmp-subhead">{product.shortLabel}</h3>
                    <OfferList
                      sideKey={product.key}
                      shortLabel={product.shortLabel}
                      emptyOffersMessage={product.emptyOffersMessage}
                      side={sides[product.key] || INITIAL_SIDE}
                      productSlug={product.slug}
                      comparisonId={config.id}
                    />
                    <Link className="ab-cmp-text-link ab-cmp-text-link-block" to={productPdpPath(product)}>
                      {product.shortLabel} product page
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </details>

          <details className="ab-cmp-disclosure-panel" data-testid="package-disclosure">
            <summary>LVS42HD vs LVS42HD-IF package details</summary>
            <div className="ab-cmp-disclosure-body">
              <p>
                LVS42HD and LVS42HD-IF share the same HD sonar-performance path. The difference is the package:
                cable length, cable type, included mount, and weight including cable.
              </p>
              <p>
                Standard LVS42HD includes an 18 ft cable, 3.6 lb transducer/cable package, adjustable Perspective
                Mode shaft mount, and trolling-motor barrel mount.
              </p>
              <p>
                LVS42HD-IF includes a 9.8 ft flexible cold-weather cable, 2.6 lb transducer/cable package,
                0-degree ice shaft mount, and all-in-one power/network cable.
              </p>
            </div>
          </details>
        </div>
      </section>
    </article>
  );
}
